<script>
import { v1 as uuid } from "uuid";

export default {
  name: 'CountdownTimer',
  props: {
    language: {
      type: String,
      required: true,
    },

    // Countdown
    enddate: {
      type: String,
      required: true,
    },
    isdaylightsaving: {
      type: String,
      required: false,
      default: false,
    },
    
    // Titles
    titlestartfrombeginning: {
      type: String,
      required: true,
    },
    titlelasttwodays: {
      type: String,
      required: false,
    },
    titlelastday: {
      type: String,
      required: false,
    },
    titlecountdownisover: {
      type: String,
      required: true,
    },
    
    // Description
    description: {
      type: String,
      required: false,
    },

    // Block Colours
    // yellow, blue, grey, green, orange, pink, purple, red
    colourstartfrombeginning: {
      type: String,
      required: true,
      default: 'yellow',
    },
    colourlasttwodays: {
      type: String,
      required: true,
      default: 'yellow',
    },
    colourlastday: {
      type: String,
      required: true,
      default: 'orange',
    },
    colourcountdownisover: {
      type: String,
      required: true,
      default: 'grey',
    },

    // CTA buttons
    btnonehref: {
      type: String,
      required: false
    },
    btnonelabel: {
      type: String,
      required: false
    },
    btnoneicon: {
      type: String,
      required: false
    },
    btnonetheme: {
      type: String,
      required: false
    },
    btnonetarget: {
      type: String,
      required: false
    },
    btntwohref: {
      type: String,
      required: false
    },
    btntwolabel: {
      type: String,
      required: false
    },
    btntwoicon: {
      type: String,
      required: false
    },
    btntwotheme: {
      type: String,
      required: false
    },
    btntwotarget: {
      type: String,
      required: false
    },
  },
  data() {
    return {
      id: uuid(),
      isFrenchLanguage: this.language == "fr" ? true : false,
      countdowntitle: this.titlestartfrombeginning,
      countdownblockcolour: 'theme--' + this.colourstartfrombeginning,
      daysCounter: 0,
      hoursCounter: 0,
      minutesCounter: 0,
      secondsCounter: 0,
      daysLabel: "DAYS",
      hoursLabel: "HOURS",
      minutesLabel: "MINUTES",
      secondsLabel: "SECONDS",
      isExperienceEditor: false,
      isHeartbeating: false,
      isdaylightsaving: true,
    };
  },
  mounted() {
    this.startCountdown();
    this.checkEditor();
    this.startHeartbeat();
  },
  methods: {
    startHeartbeat() {
      //starts the heartbeat animation on the CTA buttons if they are blue
      if (this.btnoneicon === 'True' && this.btnonetheme === 'btn--blue') {
        const btnOne = document.querySelector('.countdown-timer__button.btn--blue.btn--one');
        if (btnOne) {
          btnOne.classList.add('btn__heartbeat');
        }
      }
      if (this.btnoneicon === 'True' && this.btntwoicon === 'True' && this.btntwotheme === 'btn--blue') {
        const btnTwo = document.querySelector('.countdown-timer__button.btn--blue.btn--two');
        if (btnTwo) {
          btnTwo.classList.add('btn__heartbeat');
        }
      }
      setTimeout(() => {
        const buttons = document.querySelectorAll('.countdown-timer__button.btn--blue');
        buttons.forEach(button => {
          button.classList.remove('btn__heartbeat');
        });
      }, 8000);
    },

    //let the component know if it is in Sitecore's experience editor, it helps with creating a better editing experience
    checkEditor() {
      if (document.body.classList.contains('is-experience-editor')) {
        this.isExperienceEditor = true;
      }
    },

    startCountdown() {
      //sitecore date format adds the timezone offset to the selected date. We need this number to calculate the countdown correctly
      this.timezoneOffset = parseInt(this.enddate.slice(-4, -3));
      
      const eventDate = Date.parse(this.enddate) / 1000;
      const interval = setInterval(() => {
        const currentDate = Math.floor(Date.now() / 1000);
        //calculate the difference between the event date and the current date plus the timezone offset
        let seconds = eventDate - currentDate - (this.timezoneOffset * 60 * 60);

        if (seconds < 0) {
          this.updateCountdown(0, 0, 0, 0);
          this.countdowntitle = this.titlecountdownisover;
          clearInterval(interval);
          return;
        }

        const days = Math.floor(seconds / (60 * 60 * 24));
        seconds -= days * 60 * 60 * 24;

        const hours = Math.floor(seconds / (60 * 60));
        seconds -= hours * 60 * 60;

        const minutes = Math.floor(seconds / 60);
        seconds -= minutes * 60;

        this.updateCountdown(days, hours, minutes, seconds);
      }, 1000);
    },
    updateCountdown(days, hours, minutes, seconds) {
      this.daysCounter = days;
      this.hoursCounter = hours;
      this.minutesCounter = minutes;
      this.secondsCounter = seconds;

      if (this.isFrenchLanguage) {
        this.daysLabel = days === 1 ? "JOUR" : "JOURS";
        this.hoursLabel = hours === 1 ? "HEURE" : "HEURES";
        this.minutesLabel = minutes === 1 ? "MINUTE" : "MINUTES";
        this.secondsLabel = seconds === 1 ? "SECONDE" : "SECONDES";
      } else {
        this.daysLabel = days === 1 ? "DAY" : "DAYS";
        this.hoursLabel = hours === 1 ? "HOUR" : "HOURS";
        this.minutesLabel = minutes === 1 ? "MINUTE" : "MINUTES";
        this.secondsLabel = seconds === 1 ? "SECOND" : "SECONDS";
      }

      if (days === 1) {
        //Less than 48 hours
        this.countdowntitle = this.titlelasttwodays;
        this.countdownblockcolour = 'theme--' + this.colourlasttwodays || "yellow";
      } else if (days === 0 && (hours > 0 || minutes > 0 || seconds > 0)) {
        //Less than 24 hours
        this.countdowntitle = this.titlelastday;
        this.countdownblockcolour = 'theme--' + this.colourlastday || "orange";
      } else if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
        //countdown is over
        this.countdowntitle = this.titlecountdownisover;
        this.countdownblockcolour = 'theme--' + this.colourcountdownisover || "light-grey";
      } else {
        //Starter value - more than 48 hours
        this.countdowntitle = this.titlestartfrombeginning;
        this.countdownblockcolour = 'theme--' + this.colourstartfrombeginning || "yellow";
      }
    },
  },
};
</script>
<template>
  <section class="section countdown-timer theme--white">
    <div class="container-fluid">
      <header v-if="countdowntitle || description || isExperienceEditor" class="section__header">
          <div class="row">
              <div class="generic-text__header">
                  <div class="text-block">
                      <h2 class="title">
                          {{ countdowntitle }}
                      </h2>
                  </div>
              </div>
              <div v-if="description || isExperienceEditor" class="generic-text__content">
                  <div class="text-block">
                      <div class="wysiwyg">
                          {{ description }}
                      </div>
                  </div>
              </div>
          </div>
      </header>
      <div class="section__content">
        <div class="countdown-timer__clock" :id="id + '_countdown'">
          <div class="countdown-timer__block">
            <span :class="countdownblockcolour">{{ daysCounter }}</span>
            <p>{{ daysLabel }}</p>
          </div>
          <div class="countdown-timer__block">
            <span :class="countdownblockcolour">{{ hoursCounter }}</span>
            <p class="timeRefHours">{{ hoursLabel }}</p>
          </div>
          <div class="countdown-timer__block">
            <span :class="countdownblockcolour">{{ minutesCounter }}</span>
            <p class="timeRefMinutes">{{ minutesLabel }}</p>
          </div>
          <div class="countdown-timer__block">
            <span :class="countdownblockcolour">{{ secondsCounter }}</span>
            <p class="timeRefSeconds">{{ secondsLabel }}</p>
          </div>
        </div>
        
        
        <div v-if="btnonehref && btnonelabel || btntwohref && btntwolabel" class="countdown-timer__buttons">
          <a
          v-if="btnonehref && btnonelabel"
          class="btn countdown-timer__button btn--one"
          :href="btnonehref"
          :class="btnonetheme"
          :target="btnonetarget"
          >
            <span class="btn__content">
              <span class="btn__text">{{ btnonelabel }}</span>
              <svg v-if="btnoneicon == 'True' && btnonetheme == 'btn--blue'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <path fill="#ffd91b" d="M26.3 5.2c-2.7-2.9-7.1-2.9-9.9-.3l-.3.3L15 6.3l-1.1-1.2C12.6 3.8 10.7 3 8.8 3S5 3.8 3.7 5.2C.9 8.1.9 12.8 3.7 15.7l10.4 11c.2.3.5.4.8.4s.6-.1.8-.4l.1-.1 10.4-11c2.9-2.8 2.9-7.5.1-10.4z" class="donation-inside"></path><path fill="#ffd91b" d="M27.7 4c-3-3.2-8-3.3-11.1-.3l-.3.3L15 5.3 13.8 4c-1.5-1.6-3.6-2.4-5.7-2.4-2.2-.1-4.3.8-5.8 2.4-3.1 3.3-3.1 8.5 0 11.8L14 28.1c.2.3.6.4.9.4s.7-.1.9-.4l.1-.1 11.7-12.3c3.2-3.2 3.2-8.4.1-11.7zm-1.9 10L15 25.2 4.2 14a5.92 5.92 0 0 1 0-8.2c.9-1 2.2-1.6 3.6-1.7H8c1.5 0 2.8.6 3.8 1.7L14 8.1c.2.3.6.4.9.4s.7-.1.9-.4l.1-.1 2.2-2.3c1.9-2.1 5.2-2.3 7.3-.3l.3.3c2.2 2.4 2.2 6 .1 8.3z"></path>
              </svg>
            </span>
          </a>
          <a
            v-if="btntwohref && btntwolabel"
            class="btn countdown-timer__button btn--two"
            :href="btntwohref"
            :class="btntwotheme"
            :target="btntwotarget"
          >
            <span class="btn__content">
              <span class="btn__text">{{ btntwolabel }}</span>
              <svg v-if="btntwoicon == 'True' && btntwotheme == 'btn--blue'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <path fill="#ffd91b" d="M26.3 5.2c-2.7-2.9-7.1-2.9-9.9-.3l-.3.3L15 6.3l-1.1-1.2C12.6 3.8 10.7 3 8.8 3S5 3.8 3.7 5.2C.9 8.1.9 12.8 3.7 15.7l10.4 11c.2.3.5.4.8.4s.6-.1.8-.4l.1-.1 10.4-11c2.9-2.8 2.9-7.5.1-10.4z" class="donation-inside"></path><path fill="#ffd91b" d="M27.7 4c-3-3.2-8-3.3-11.1-.3l-.3.3L15 5.3 13.8 4c-1.5-1.6-3.6-2.4-5.7-2.4-2.2-.1-4.3.8-5.8 2.4-3.1 3.3-3.1 8.5 0 11.8L14 28.1c.2.3.6.4.9.4s.7-.1.9-.4l.1-.1 11.7-12.3c3.2-3.2 3.2-8.4.1-11.7zm-1.9 10L15 25.2 4.2 14a5.92 5.92 0 0 1 0-8.2c.9-1 2.2-1.6 3.6-1.7H8c1.5 0 2.8.6 3.8 1.7L14 8.1c.2.3.6.4.9.4s.7-.1.9-.4l.1-.1 2.2-2.3c1.9-2.1 5.2-2.3 7.3-.3l.3.3c2.2 2.4 2.2 6 .1 8.3z"></path>
              </svg>
            </span>
          </a>
        </div>  
      </div>
    </div>
  </section>
</template>
